import './CalculatorResults.css';
import React from 'react';
import star from './images/star.svg';
import alert from './images/alert-triangle.svg';
import arrowUp from './images/arrow-up.svg';
import arrowDown from './images/arrow-down.svg';

function CalculatorResults({isAdvanced, withConsolidationAmount, withConsolidationMonths, withoutHelpAmount, withoutHelpMonths, withSeraBasicAmount, withSeraBasicMonths, withSeraAmount, withSeraMonths, errorMsg, handleEstimatesButtonClick, creditScore, monthlyPayment, minimumMonthlyPayment, hasMinimumPaymentError, loanAPR}) {

  const noConsolidationLoanTooltip = `Debt consolidation isn't usually available for credit scores under 580. Sera helps improve your credit, unlocking additional debt reduction options in the future.`

  const formatAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format;

  function getHeadline() {
    switch (creditScore) {
      case '720-850':
      case '660-719':
        return (
          <p className='headline'>With <span className='accent'>Sera</span> you pay significantly less interest in a shorter period of time.</p>
        );
      case '580-659':
        return (
          <p className='headline'>With <span className='accent'>Sera</span> you pay less interest in a shorter period of time, while also building your <span className='accent'>credit score</span>.</p>
        );
      case '300-579':
        return (
          <p className='headline'>With scores below 580, <span className='accent'>Sera</span> will focus on improving your credit to unlock more powerful debt elimination tools.</p>
        );
        default:
          break;  
    }
    return '';
  }

  function getResultWidthPerentage(amount) {
    let maxAmount = (Math.ceil(withoutHelpAmount / 100) * 100);
    let widthPercentage = ((amount)/maxAmount) * 100;
    //console.log('maxAmount: ' + maxAmount + ', amount: ' + amount + ', widthPercentage: ' + widthPercentage);
    return widthPercentage;
  }

  function getResultWidthStyle(amount) {
    return {width: `${getResultWidthPerentage(amount)}%`, transition: 'width 1s'};
  }

  const tooltipBlurbDisplayThresholdPrecentage = 46;
  function getSeraTooltipDisplay() {
    if (hasMinimumPaymentError) {
      return {opacity: '0'};
    }

    let widthPercentage = getResultWidthPerentage(withSeraAmount);
    return {opacity: widthPercentage > tooltipBlurbDisplayThresholdPrecentage ? '0' : '1', transition: 'opacity 2s'};
  }

  function getSeraBlurbDisplay() {
    if (hasMinimumPaymentError) {
      return {opacity: '0'};
    }
    let widthPercentage = getResultWidthPerentage(withSeraAmount);
    return {opacity: widthPercentage < tooltipBlurbDisplayThresholdPrecentage ? '0' : '1', transition: 'opacity 2s'};
  }

  function showDebtConsolidationLoan() {
    return (isAdvanced || (creditScore !== '300-579'));
  }

  function CalculatorResultsSummary() {
    function getAmountSaved() {
      if (hasMinimumPaymentError) {
        return '-';
      }
      return formatAmount(withoutHelpAmount - withSeraAmount);
    }

    function getTimeSaved() {
      if (hasMinimumPaymentError) {
        return '-';
      }
      let diff = withoutHelpMonths - withSeraMonths;
      return `${diff} ${diff === 1 ? 'month' : 'months'}`;
    }

    return (
      <div className='calculator-results-summary-container'>
        <p>Benefits of using <span className='bold accent'>Sera</span></p>
        <div className='benefits-container'>
          <div>
            <img src={arrowUp} alt='' />
            <div>Increase your credit score</div>
          </div>
          <div>
            <img src={arrowDown} alt='' />
            <div>Pay less interest</div>
          </div>
          <div>
            <img src={arrowUp} alt='' />
            <div>Pay faster</div>
          </div>
        </div>
        <div className='calculator-results-summary'>
            <div>
              <div>Money saved</div>
              <div>{getAmountSaved()}</div>
            </div>
            <div></div>
            <div>
              <div>Time saved</div>
              <div>{getTimeSaved()}</div>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className='calculator-results'>
      {getHeadline()}
      
      <div className='calculator-results-graph-container'>

        <div style={{position: "relative"}}>
          
          <div className={`calculator-results-graph ${hasMinimumPaymentError ? 'error' : ''}`}>

            <div style={getResultWidthStyle(withSeraAmount)}>
              <div className='calculator-results-graph-y-axis vertical-center accent' style={{opacity:'1'}}>With Sera</div>
              <div className='calculator-results-graph-tooltip vertical-center' style={getSeraTooltipDisplay()}>
                <img src={star} alt='star' />
                <p className='vertical-center'>With Sera you pay <span className='bold'>{formatAmount(withSeraAmount)}</span> interest in just <span className='bold'>{withSeraMonths} months</span></p>
              </div>
              <div className='calculator-results-blurb vertical-center white' style={getSeraBlurbDisplay()}>
                <img src={star} alt='star' />
                <p>With Sera you pay <span className='bold'>{formatAmount(withSeraAmount)}</span> interest in <span className='bold'>{withSeraMonths} months</span></p>
              </div>
            </div>

            {isAdvanced && (
              <div style={getResultWidthStyle(withSeraBasicAmount)}>
                <div className='calculator-results-graph-y-axis vertical-center'>With Sera Basic</div>
                <p className='calculator-results-blurb vertical-center'>
                  Pay <span className='bold'>{formatAmount(withSeraBasicAmount)}</span> interest<br/>
                  in <span className='bold'>{withSeraBasicMonths} months</span>
                </p>
              </div>            
            )}
            
            {(showDebtConsolidationLoan()) ? (
              <div style={getResultWidthStyle(withConsolidationAmount)}>
                <div className='calculator-results-graph-y-axis vertical-center'>With debt consolidation</div>
                <p className='calculator-results-blurb vertical-center'>
                  Pay <span className='bold'>{formatAmount(withConsolidationAmount)}</span> interest<br/>
                  in <span className='bold'>{withConsolidationMonths} months</span>
                </p>
              </div>
            ) : (
              <div className='no-consolidation-loan-container' style={getResultWidthStyle(withoutHelpAmount)}>
                <div className='vertical-center' style={{width: '100%'}}>
                  <div className='calculator-results-graph-y-axis vertical-center'>With debt consolidation</div>
                  <div className='no-consolidation-loan'>
                    <img src={alert} alt='alert' />
                    <div className='tooltip-container'>
                      <p className='title1'>Not available for credit-scores under 580.&nbsp;<br/>
                      <span className='tooltip1 link' data-text={noConsolidationLoanTooltip}>Learn more.</span></p>
                    </div>
                  </div>
                </div>
              </div>
            )}
              
            <div style={getResultWidthStyle(withoutHelpAmount)}>
              <div className='calculator-results-graph-y-axis vertical-center'>Without any help</div>
              <p className='calculator-results-blurb vertical-center'>
                Pay <span className='bold'>{formatAmount(withoutHelpAmount)}</span> interest<br/>
                in <span className='bold'>{withoutHelpMonths} months</span>
              </p>
            </div>

          </div>

          <p className='calculator-results-graph-x-axis'>$ Amount paid</p>

          <div className={`minimum-payment-error ${hasMinimumPaymentError ? 'show' : ''} horizontal-center`}>
            <div className='title'>
              <img src={alert} alt='alert' /><p>Review your monthly payment</p>
            </div>
            <div className='msg'>
              <div className='amt'>{formatAmount(monthlyPayment)}</div>
              <div>Minimum payment needs <br/>to be over {formatAmount(minimumMonthlyPayment)}</div>
            </div>
          </div>
          
        </div>

      </div>

      <CalculatorResultsSummary />
    </div>
  );
}

export default CalculatorResults;